// Types shared by the Worker (API) and the browser app.
// Keep this file free of runtime imports.

export type GiftTerm = '3m' | '6m';

export const GIFT_TERM_DAYS: Record<GiftTerm, number> = {
  '3m': 90,
  '6m': 180,
};

export const GIFT_TERM_LABEL: Record<GiftTerm, string> = {
  '3m': '3 months',
  '6m': '6 months',
};

export type GiftStatus =
  | 'pending' // intent created, not paid
  | 'paid' // paid, gift email not sent yet (scheduled)
  | 'sent' // gift email delivered to recipient (or gifter if send-to-self)
  | 'redeemed'
  | 'refunded'
  | 'expired';

export interface CreateGiftIntentRequest {
  term: GiftTerm;
  gifterName: string;
  gifterEmail: string;
  recipientName: string;
  /** Required unless sendToSelf is true. */
  recipientEmail?: string;
  sendToSelf: boolean;
  message?: string;
  /** ISO date (YYYY-MM-DD) in the gifter's local calendar, or omitted for "send now". */
  deliverOn?: string;
  /** IANA timezone of the gifter, used to turn deliverOn into an instant (09:00 local). */
  timeZone?: string;
}

export interface CreateGiftIntentResponse {
  giftId: string;
  manageToken: string;
  priceId: string;
}

export interface GiftStatusResponse {
  giftId: string;
  status: GiftStatus;
  term: GiftTerm;
  recipientName: string;
  recipientEmail: string | null;
  sendToSelf: boolean;
  deliverAt: string;
  sentAt: string | null;
  /** Only returned when status is paid/sent and the request carries a valid manage token. */
  code?: string;
  redeemUrl?: string;
}

export type CodeLookupStatus =
  | 'valid'
  | 'not_found'
  | 'not_paid'
  | 'already_redeemed'
  | 'refunded'
  | 'expired';

export interface CodeLookupResponse {
  status: CodeLookupStatus;
  term?: GiftTerm;
  termDays?: number;
  gifterName?: string;
  recipientName?: string;
  recipientEmail?: string | null;
  message?: string | null;
}

export interface RedeemRequest {
  code: string;
  email: string;
  firstName: string;
  /** Same answer keys and value formats as the marketing quiz (multi-select = comma-joined). */
  quizAnswers: Record<string, string>;
}

export interface RedeemResponse {
  appUrl: string;
  accessUntil: string;
  /** True when the user already had an active subscription and the gift was appended after it. */
  deferred: boolean;
}

export interface ApiError {
  error: string;
  code?: string;
}
